import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter, Stack } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { useScoreHistory, type ScoreSummaryByType } from "@/modules/scores";

interface TeamMember {
  user_id: string;
  full_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  is_captain: boolean;
  points_contributed: number;
}

interface TeamData {
  id: string;
  name: string;
  color: string | null;
  avatar_url: string | null;
  description: string | null;
  total_score: number;
}

/**
 * Team Score Detail Screen
 * Shows team info, score breakdown by game type, history, and member contributions
 */
export default function TeamScoreDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const colors = useThemeColors();

  const [team, setTeam] = useState<TeamData | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const {
    history,
    summaryByType,
    loading: historyLoading,
    refresh: refreshHistory,
  } = useScoreHistory({ teamId: id, limit: 100 });

  /**
   * Fetch team data and members
   */
  const fetchTeamData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Fetch team info
      const { data: teamData, error: teamError } = await supabase
        .from("teams")
        .select("id, name, color, avatar_url, description, total_score")
        .eq("id", id)
        .single();

      if (teamError) throw teamError;
      setTeam(teamData);

      // Fetch team members
      const { data: membersData, error: membersError } = await supabase
        .from("team_members")
        .select(`
          user_id,
          is_captain,
          profiles:user_id (
            full_name,
            nickname,
            avatar_url
          )
        `)
        .eq("team_id", id);

      if (membersError) throw membersError;

      // Get score contributions per member
      const { data: scoreData, error: scoreError } = await supabase
        .from("score_history")
        .select("awarded_to, points")
        .eq("team_id", id)
        .not("awarded_to", "is", null);

      if (scoreError) throw scoreError;

      // Sum points per user
      const pointsMap: Record<string, number> = {};
      (scoreData || []).forEach((s) => {
        if (s.awarded_to) {
          pointsMap[s.awarded_to] = (pointsMap[s.awarded_to] || 0) + s.points;
        }
      });

      // Map members with contributions
      const mappedMembers: TeamMember[] = (membersData || [])
        .map((m: any) => ({
          user_id: m.user_id,
          full_name: m.profiles?.full_name,
          nickname: m.profiles?.nickname,
          avatar_url: m.profiles?.avatar_url,
          is_captain: m.is_captain,
          points_contributed: pointsMap[m.user_id] || 0,
        }))
        .sort((a, b) => b.points_contributed - a.points_contributed);

      setMembers(mappedMembers);
    } catch (error) {
      console.error("Error fetching team data:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchTeamData(), refreshHistory()]);
    setRefreshing(false);
  }, [fetchTeamData, refreshHistory]);

  /**
   * Get initials from name
   */
  const getInitials = (name: string | null): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Get icon for game type
   */
  const getGameTypeIcon = (
    gameType: string
  ): keyof typeof Ionicons.glyphMap => {
    switch (gameType.toLowerCase()) {
      case "quiz":
        return "help-circle-outline";
      case "speurtocht":
        return "search-outline";
      case "challenge":
      case "uitdaging":
        return "flash-outline";
      case "photo":
      case "foto":
        return "camera-outline";
      case "boter_kaas_eieren":
      case "tic_tac_toe":
        return "grid-outline";
      case "puzzle_matching":
      case "memory":
        return "images-outline";
      case "chess":
      case "schaken":
        return "extension-puzzle-outline";
      case "endless_runner":
        return "walk-outline";
      case "sudoku":
        return "grid-outline";
      case "match_3":
        return "diamond-outline";
      case "dammen":
        return "ellipse-outline";
      case "manual":
        return "hand-left-outline";
      default:
        return "trophy-outline";
    }
  };

  if (loading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Text style={{ color: colors.textSecondary }}>Laden...</Text>
      </SafeAreaView>
    );
  }

  if (!team) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name="alert-circle-outline" size={48} color={colors.error} />
        <Text className="mt-4" style={{ color: colors.text }}>
          Team niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  // Calculate max points for progress bar scaling
  const maxTypePoints = Math.max(...summaryByType.map((s) => s.total_points), 1);

  return (
    <>
      <Stack.Screen options={{ title: team.name }} />
      <SafeAreaView
        className="flex-1"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
        >
          {/* Team Header */}
          <View
            className="mx-4 mt-4 rounded-2xl p-6 items-center"
            style={{ backgroundColor: team.color || colors.primary }}
          >
            {team.avatar_url ? (
              <Image
                source={{ uri: team.avatar_url }}
                className="w-20 h-20 rounded-2xl mb-3"
                resizeMode="cover"
              />
            ) : (
              <View
                className="w-20 h-20 rounded-2xl items-center justify-center mb-3"
                style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
              >
                <Text className="text-white text-3xl font-bold">
                  {team.name.charAt(0)}
                </Text>
              </View>
            )}
            <Text className="text-white text-2xl font-bold">{team.name}</Text>
            {team.description && (
              <Text className="text-white/80 mt-1 text-center">
                {team.description}
              </Text>
            )}
            <View className="flex-row items-center mt-4">
              <Ionicons name="trophy" size={24} color="#FFD700" />
              <Text className="text-white text-3xl font-bold ml-2">
                {team.total_score.toLocaleString()}
              </Text>
              <Text className="text-white/80 ml-2">punten</Text>
            </View>
          </View>

          {/* Score Breakdown by Type */}
          {summaryByType.length > 0 && (
            <View className="mx-4 mt-6">
              <Text
                className="text-lg font-bold mb-3"
                style={{ color: colors.text }}
              >
                Score per Categorie
              </Text>
              <View
                className="rounded-xl p-4"
                style={{ backgroundColor: colors.surface }}
              >
                {summaryByType.map((item, index) => (
                  <View
                    key={item.game_type}
                    className={index > 0 ? "mt-4" : ""}
                  >
                    <View className="flex-row items-center justify-between mb-2">
                      <View className="flex-row items-center">
                        <Ionicons
                          name={getGameTypeIcon(item.game_type)}
                          size={18}
                          color={colors.primary}
                        />
                        <Text
                          className="ml-2 font-medium"
                          style={{ color: colors.text }}
                        >
                          {item.game_type}
                        </Text>
                      </View>
                      <Text
                        className="font-semibold"
                        style={{ color: colors.primary }}
                      >
                        {item.total_points.toLocaleString()}
                      </Text>
                    </View>
                    <View
                      className="h-2 rounded-full overflow-hidden"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <View
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: colors.primary,
                          width: `${(item.total_points / maxTypePoints) * 100}%`,
                        }}
                      />
                    </View>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Team Members */}
          <View className="mx-4 mt-6">
            <Text
              className="text-lg font-bold mb-3"
              style={{ color: colors.text }}
            >
              Teamleden ({members.length})
            </Text>
            <View
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: colors.surface }}
            >
              {members.map((member, index) => (
                <TouchableOpacity
                  key={member.user_id}
                  className="flex-row items-center p-4"
                  style={{
                    borderTopWidth: index > 0 ? 1 : 0,
                    borderTopColor: colors.border,
                  }}
                  activeOpacity={0.7}
                  onPress={() => router.push(`/scores/user/${member.user_id}`)}
                >
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3 overflow-hidden"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    {member.avatar_url ? (
                      <Image
                        source={{ uri: member.avatar_url }}
                        className="w-full h-full"
                        resizeMode="cover"
                      />
                    ) : (
                      <Text
                        className="font-bold"
                        style={{ color: colors.primary }}
                      >
                        {getInitials(member.nickname || member.full_name)}
                      </Text>
                    )}
                  </View>
                  <View className="flex-1">
                    <View className="flex-row items-center">
                      <Text
                        className="font-medium"
                        style={{ color: colors.text }}
                      >
                        {member.nickname || member.full_name || "Onbekend"}
                      </Text>
                      {member.is_captain && (
                        <View
                          className="ml-2 px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: colors.warningLight }}
                        >
                          <Text
                            className="text-xs font-medium"
                            style={{ color: colors.warning }}
                          >
                            Kapitein
                          </Text>
                        </View>
                      )}
                    </View>
                    {member.points_contributed > 0 && (
                      <Text
                        className="text-sm"
                        style={{ color: colors.textSecondary }}
                      >
                        {member.points_contributed.toLocaleString()} punten
                        bijgedragen
                      </Text>
                    )}
                  </View>
                  <Ionicons
                    name="chevron-forward"
                    size={20}
                    color={colors.textTertiary}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Score History */}
          <View className="mx-4 mt-6 mb-8">
            <Text
              className="text-lg font-bold mb-3"
              style={{ color: colors.text }}
            >
              Score Geschiedenis
            </Text>
            {history.length === 0 ? (
              <View
                className="rounded-xl p-6 items-center"
                style={{ backgroundColor: colors.surface }}
              >
                <Ionicons
                  name="time-outline"
                  size={32}
                  color={colors.textTertiary}
                />
                <Text
                  className="mt-2"
                  style={{ color: colors.textSecondary }}
                >
                  Nog geen score geschiedenis
                </Text>
              </View>
            ) : (
              <View
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: colors.surface }}
              >
                {history.slice(0, 20).map((entry, index) => (
                  <View
                    key={entry.id}
                    className="flex-row items-center p-4"
                    style={{
                      borderTopWidth: index > 0 ? 1 : 0,
                      borderTopColor: colors.border,
                    }}
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{
                        backgroundColor:
                          entry.points >= 0
                            ? colors.successLight
                            : colors.errorLight,
                      }}
                    >
                      <Ionicons
                        name={entry.points >= 0 ? "add" : "remove"}
                        size={20}
                        color={
                          entry.points >= 0 ? colors.success : colors.error
                        }
                      />
                    </View>
                    <View className="flex-1">
                      <Text
                        className="font-medium"
                        style={{ color: colors.text }}
                        numberOfLines={1}
                      >
                        {entry.reason}
                      </Text>
                      <View className="flex-row items-center mt-0.5">
                        <Text
                          className="text-sm"
                          style={{ color: colors.textSecondary }}
                        >
                          {formatDate(entry.created_at)}
                        </Text>
                        {entry.awarded_to_name && (
                          <Text
                            className="text-sm ml-2"
                            style={{ color: colors.textTertiary }}
                          >
                            • {entry.awarded_to_name}
                          </Text>
                        )}
                      </View>
                    </View>
                    <Text
                      className="font-bold text-lg"
                      style={{
                        color:
                          entry.points >= 0 ? colors.success : colors.error,
                      }}
                    >
                      {entry.points >= 0 ? "+" : ""}
                      {entry.points}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  );
}

